import { cn } from '@shadcn/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@shadcn/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/ui/popover';
import { CheckIcon, ChevronsUpDown, XIcon } from 'lucide-react';
import React from 'react';

import { Badge } from '../../../shadcn/components/ui/badge';
import { Label } from '../label/label';

import {
  IMultiSelectContentProps,
  IMultiSelectContext,
  IMultiSelectOptionProps,
  IMultiSelectProps,
  IMultiSelectTriggerProps,
  IMultiSelectViewProps,
  ISelectedValuesProps,
} from './types';
import { useMultiSelectSetup } from './utils';

const MultiSelectContext = React.createContext<IMultiSelectContext | null>(null);

const useMultiSelectContext = () => {
  const ctx = React.useContext(MultiSelectContext);
  if (!ctx) throw new Error('MultiSelect components must be used within MultiSelect');
  return ctx;
};

const MultiSelectOption = ({ value, className, children, ...props }: IMultiSelectOptionProps) => {
  const { isSelected, toggleOption } = useMultiSelectContext();
  const selected = isSelected(value);

  return (
    <CommandItem
      aria-selected={selected}
      className={cn('gap-2', className)}
      onSelect={() => toggleOption(value)}
      role="option"
      value={value}
      {...props}
    >
      <span>{children}</span>
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <CheckIcon className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')} />
      </span>
    </CommandItem>
  );
};

const MultiSelectContent = ({
  labelId,
  commandListRef,
  commandValue,
  onCommandValueChange,
  children,
}: IMultiSelectContentProps) => {
  const handleCommandKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ') return;

    event.preventDefault();
    const activeItem = commandListRef.current?.querySelector('[cmdk-item][aria-selected="true"]') as HTMLElement | null;
    activeItem?.dispatchEvent(new Event('cmdk-item-select'));
  };

  return (
    <PopoverContent
      align="start"
      aria-labelledby={labelId}
      className="bg-neutral-0 border-neutral-400 text-typography p-0 w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]"
    >
      <Command onKeyDown={handleCommandKeyDown} onValueChange={onCommandValueChange} value={commandValue}>
        <CommandList aria-multiselectable="true" id={`${labelId}-listbox`} ref={commandListRef} role="listbox">
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup>{children}</CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );
};

const RemoveAction = ({ onRemove, ariaLabel }: { readonly onRemove: () => void; readonly ariaLabel: string }) => (
  <div
    aria-label={ariaLabel}
    className="inline-flex items-center justify-center"
    onClick={(event) => {
      event.stopPropagation();
      onRemove();
    }}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        onRemove();
      }
    }}
    role="button"
    tabIndex={0}
  >
    <XIcon aria-hidden="true" className="size-3" />
  </div>
);

const SelectedValues = ({
  getLabel,
  maxVisible,
  placeholder,
  removeOption,
  removeRemainingOptions,
  selectedValues,
}: ISelectedValuesProps) => {
  if (!selectedValues.length) return <span className="text-muted-foreground truncate">{placeholder}</span>;

  const visibleValues = selectedValues.slice(0, maxVisible);
  const remaining = selectedValues.length - visibleValues.length;

  return (
    <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
      <span className="flex flex-nowrap items-center gap-1 overflow-hidden">
        {visibleValues.map((val) => (
          <Badge className="flex items-center gap-1 shrink-0" key={val} variant="outline">
            {getLabel(val)}
            <RemoveAction ariaLabel={`Remove option ${val}`} onRemove={() => removeOption(val)} />
          </Badge>
        ))}
        {remaining ? (
          <Badge className="flex items-center gap-1 shrink-0" variant="outline">
            + {remaining}
            <RemoveAction ariaLabel="Clear remaining options" onRemove={() => removeRemainingOptions()} />
          </Badge>
        ) : null}
      </span>
    </span>
  );
};

const MultiSelectTrigger = ({
  disabled,
  label,
  labelId,
  maxVisible,
  open,
  placeholder,
  selectId,
  triggerStyles,
  getLabel,
  removeOption,
  removeRemainingOptions,
  selectedValues,
}: ISelectedValuesProps & IMultiSelectTriggerProps) => (
  <PopoverTrigger asChild>
    <button
      aria-controls={`${labelId}-listbox`}
      aria-disabled={disabled}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={label ?? placeholder}
      aria-labelledby={labelId}
      className={cn(
        triggerStyles,
        'flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm shadow-xs outline-none',
        disabled ? 'cursor-not-allowed opacity-50 pointer-events-none' : ''
      )}
      disabled={disabled}
      id={selectId}
      tabIndex={disabled ? -1 : 0}
      type="button"
    >
      <SelectedValues
        getLabel={getLabel}
        maxVisible={maxVisible}
        placeholder={placeholder}
        removeOption={removeOption}
        removeRemainingOptions={removeRemainingOptions}
        selectedValues={selectedValues}
      />
      <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" />
    </button>
  </PopoverTrigger>
);

const MultiSelectView = ({
  children,
  disabled,
  label,
  maxVisible,
  placeholder,
  commandListRef,
  commandValue,
  contextValue,
  getLabel,
  labelId,
  open,
  removeOption,
  removeRemainingOptions,
  selectId,
  selectedValues,
  setCommandValue,
  setOpen,
  triggerStyles,
}: IMultiSelectViewProps) => {
  return (
    <MultiSelectContext.Provider value={contextValue}>
      <div className="max-w-xs space-y-2">
        {label && (
          <Label htmlFor={selectId} id={labelId}>
            {label}
          </Label>
        )}
        <Popover onOpenChange={setOpen} open={open}>
          <MultiSelectTrigger
            disabled={disabled}
            getLabel={getLabel}
            label={label}
            labelId={labelId}
            maxVisible={maxVisible}
            open={open}
            placeholder={placeholder}
            removeOption={removeOption}
            removeRemainingOptions={removeRemainingOptions}
            selectId={selectId}
            selectedValues={selectedValues}
            triggerStyles={triggerStyles}
          />
          <MultiSelectContent
            commandListRef={commandListRef}
            commandValue={commandValue}
            labelId={labelId}
            onCommandValueChange={setCommandValue}
          >
            {children}
          </MultiSelectContent>
        </Popover>
      </div>
    </MultiSelectContext.Provider>
  );
};

const DEFAULT_MAX_VISIBLE = 3;

function MultiSelect({
  defaultValue = [],
  maxVisible = DEFAULT_MAX_VISIBLE,
  id,
  placeholder = 'Select options',
  value,
  onValueChange,
  disabled,
  error,
  label,
  children,
  className,
}: IMultiSelectProps) {
  const setupProps = useMultiSelectSetup({
    children,
    className,
    defaultValue,
    error,
    id,
    maxVisible,
    onValueChange,
    value,
  });

  return (
    <MultiSelectView
      {...setupProps}
      disabled={disabled}
      label={label}
      maxVisible={maxVisible}
      placeholder={placeholder}
    >
      {children}
    </MultiSelectView>
  );
}

export { MultiSelect, MultiSelectOption, useMultiSelectContext };
